import { ComponentType, type IConfig } from '../../types';
import { ComponentConfigType } from '../../utils/data-parser';
import {
  BASIC_JSON_LOGIC_TOOLTIP,
  BASIC_SELECT_VALUE_TOOLTIP,
  BASIC_VALUE_TOOLTIP,
  BASIC_VARIABLE_TOOLTIP,
  BODY_TRANSFORMER_ITEMS_TOOLTIP,
  ORDER_DIRECTION_OPTIONS,
} from '../../utils';

const BASIC_LOADABLE_DATA_TOOLTIP = `
<div>
  <p>
    Enter a value or use <code>{{ }}</code> to retrieve a value from the context using <b>JMESPath</b> selectors.
  </p>
  <p>
    The expression must return a <b>string</b> or <b>number</b>.
  </p>
  <div style={{ marginTop: 8 }}>
    <b>Examples:</b>
    <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
     <li>
        <code>/core/api/model-instances/{{$row.id}}</code> - used in table row component
      </li>
       <li>
        <code>/core/api/model-instances/{{$item.id}}</code> - used in loop component, recursion component
      </li>
      <li>
        <code>/core/api/model-instances/{{$instance.id}}</code> - used in single page component
      </li>
    </ul>
  </div>
   <div style={{ marginTop: 8 }}>
    <b>Examples of endpoints:</b>
    <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
     <li>
       <code>/core/api/model-instances/{{id}}</code> - get model instance by id
      </li>
      <li>
       <code>/core/api/model-instances</code> - get all model instances
      </li>
      <li>
       <code>/core/api/model-instances</code> - get all model instances
      </li>
       <li>
       <code>/core/api/model-instances/{{id}}/cross-references/{{crossReferenceModelId}}</code> - get cross references by model instance id and cross reference model id
      </li>
       <li>
       <code>/core/api/model-instances/{{id}}/cross-references</code> - get cross references by model instance id
      </li>
    </ul>
  </div>
</div>`;

export const config: IConfig = {
  component: ComponentType.LoadableData,
  configGroups: [
    {
      title: 'Component Settings',
      items: [
        {
          name: 'requestType',
          label: 'Request Type',
          valueComponent: {
            type: 'Select',
            options: [
              { label: 'Custom', value: 'Custom' },
              { label: 'Single Model Instance', value: 'SingleModelInstance' },
              { label: 'List Model Instance', value: 'ListModelInstance' },
              {
                label: 'List Model Instance With Meta',
                value: 'ListModelInstanceWithMeta',
              },
              {
                label: 'List Model Instance Meta',
                value: 'ListModelInstanceMeta',
              },
            ],
            defaultValue: 'Custom',
          },
          configTypes: [],
        },
        {
          name: 'modelId',
          label: 'Model',
          valueComponent: {
            type: 'SingleModelSelect',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'instanceId',
          label: 'Instance ID',
          valueComponent: {
            type: 'Input',
            placeholder: 'e.g., {{$row.id}}',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.Variable,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.Variable,
              value: BASIC_VARIABLE_TOOLTIP,
            },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'modelIds',
          label: 'Model IDs',
          valueComponent: {
            type: 'ReferenceModelSelector',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'dataFilters',
          label: 'Filters',
          valueComponent: {
            type: 'DinamicDataFilters',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'page',
          label: 'Page',
          valueComponent: {
            type: 'InputNumber',
            placeholder: 'e.g., 1',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'size',
          label: 'Per Page',
          valueComponent: {
            type: 'InputNumber',
            placeholder: 'e.g., 10',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'sortName',
          label: 'Order By',
          valueComponent: {
            type: 'Input',
            placeholder: 'e.g., createdAt',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'sortOrder',
          label: 'Order Direction',
          valueComponent: {
            type: 'Select',
            options: ORDER_DIRECTION_OPTIONS,
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            {
              key: ComponentConfigType.Value,
              value: BASIC_SELECT_VALUE_TOOLTIP,
            },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'dataName',
          label: 'Data Name',
          valueComponent: {
            type: 'Input',
            placeholder: 'e.g., data',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'transformer',
          label: 'Data Transformer (JMESPath)',
          valueComponent: {
            type: 'CodeEditor',
            placeholder: 'JMESPath expression (e.g., @[?status==`active`])',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            {
              key: ComponentConfigType.Value,
              value: BODY_TRANSFORMER_ITEMS_TOOLTIP,
            },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'visible',
          label: 'Visible',
          valueComponent: {
            type: 'Select',
            options: [
              { label: 'True', value: true },
              { label: 'False', value: false },
            ],
            defaultValue: true,
          },
          configTypes: [
            ComponentConfigType.Variable,
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            {
              key: ComponentConfigType.Variable,
              value: BASIC_VARIABLE_TOOLTIP,
            },
            {
              key: ComponentConfigType.Value,
              value: BASIC_SELECT_VALUE_TOOLTIP,
            },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'url',
          label: 'URL',
          valueComponent: {
            type: 'TextArea',
            placeholder: 'e.g., https://api.example.com/data',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            {
              key: ComponentConfigType.Value,
              value: BASIC_LOADABLE_DATA_TOOLTIP,
            },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
      ],
    },
  ],
};
